import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const SearchFilters = ({ onFiltersChange, isLoading, onSearch }) => {
  const [filters, setFilters] = useState({
    leadType: 'all',
    industry: '',
    location: '',
    companySize: '',
    revenue: '',
    technology: [],
    keywords: '',
    territory: '',
    behavioralIndicators: [],
    // New SMM Deal Finder features
    socialMediaPresence: [],
    advertisingActivity: [],
    websiteQuality: '',
    gmbVerified: false,
    adSpendRange: '',
    socialFollowerRange: ''
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const industryOptions = [
    { value: '', label: 'All Industries' },
    { value: 'technology', label: 'Technology' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'finance', label: 'Financial Services' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'education', label: 'Education' },
    { value: 'real-estate', label: 'Real Estate' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'marketing', label: 'Marketing & Advertising' },
    { value: 'construction', label: 'Construction' },
    { value: 'fashion', label: 'Fashion & Apparel' },
    { value: 'beauty', label: 'Beauty & Cosmetics' },
    { value: 'automotive', label: 'Automotive' },
    { value: 'food-beverage', label: 'Food & Beverage' },
    { value: 'travel', label: 'Travel & Tourism' },
    { value: 'media', label: 'Media & Entertainment' },
    { value: 'sports', label: 'Sports & Fitness' },
    { value: 'legal', label: 'Legal Services' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'agriculture', label: 'Agriculture' },
    { value: 'logistics', label: 'Logistics & Transportation' }
  ];

  const companySizeOptions = [
    { value: '', label: 'All Sizes' },
    { value: 'startup', label: 'Startup (1-10)' },
    { value: 'small', label: 'Small (11-50)' },
    { value: 'medium', label: 'Medium (51-200)' },
    { value: 'large', label: 'Large (201-1000)' },
    { value: 'enterprise', label: 'Enterprise (1000+)' }
  ];

  const revenueOptions = [
    { value: '', label: 'All Revenue' },
    { value: '0-1m', label: '$0 - $1M' },
    { value: '1m-10m', label: '$1M - $10M' },
    { value: '10m-50m', label: '$10M - $50M' },
    { value: '50m-100m', label: '$50M - $100M' },
    { value: '100m+', label: '$100M+' }
  ];

  const territoryOptions = [
    { value: '', label: 'All Territories' },
    { value: 'north-america', label: 'North America' },
    { value: 'europe', label: 'Europe' },
    { value: 'asia-pacific', label: 'Asia Pacific' },
    { value: 'latin-america', label: 'Latin America' },
    { value: 'middle-east', label: 'Middle East' },
    { value: 'africa', label: 'Africa' }
  ];

  const technologyOptions = [
    { value: 'crm', label: 'CRM Software' },
    { value: 'marketing-automation', label: 'Marketing Automation' },
    { value: 'cloud-services', label: 'Cloud Services' },
    { value: 'e-commerce', label: 'E-commerce Platform' },
    { value: 'analytics', label: 'Analytics Tools' },
    { value: 'social-media', label: 'Social Media Tools' }
  ];

  const behavioralOptions = [
    { value: 'website-visitors', label: 'Recent Website Visitors' },
    { value: 'content-engagement', label: 'Content Engagement' },
    { value: 'email-opens', label: 'Email Opens' },
    { value: 'social-activity', label: 'Social Media Activity' },
    { value: 'job-changes', label: 'Recent Job Changes' },
    { value: 'funding-events', label: 'Funding Events' }
  ];

  // New filter options for enhanced features
  const socialMediaPlatforms = [
    { value: 'facebook', label: 'Facebook Active' },
    { value: 'instagram', label: 'Instagram Active' },
    { value: 'linkedin', label: 'LinkedIn Active' },
    { value: 'twitter', label: 'Twitter Active' },
    { value: 'youtube', label: 'YouTube Active' },
    { value: 'tiktok', label: 'TikTok Active' }
  ];

  const advertisingPlatforms = [
    { value: 'google-ads', label: 'Google Ads' },
    { value: 'facebook-ads', label: 'Facebook Ads' },
    { value: 'linkedin-ads', label: 'LinkedIn Ads' },
    { value: 'instagram-ads', label: 'Instagram Ads' }
  ];

  const websiteQualityOptions = [
    { value: '', label: 'Any Quality' },
    { value: 'excellent', label: 'Excellent (80-100)' },
    { value: 'good', label: 'Good (60-79)' },
    { value: 'fair', label: 'Fair (40-59)' },
    { value: 'poor', label: 'Poor (0-39)' }
  ];

  const adSpendRanges = [
    { value: '', label: 'Any Spend' },
    { value: '0-1k', label: '$0 - $1K/mo' },
    { value: '1k-5k', label: '$1K - $5K/mo' },
    { value: '5k-10k', label: '$5K - $10K/mo' },
    { value: '10k-50k', label: '$10K - $50K/mo' },
    { value: '50k+', label: '$50K+/mo' }
  ];

  const socialFollowerRanges = [
    { value: '', label: 'Any Following' },
    { value: '0-1k', label: '0 - 1K followers' },
    { value: '1k-10k', label: '1K - 10K followers' },
    { value: '10k-50k', label: '10K - 50K followers' },
    { value: '50k-100k', label: '50K - 100K followers' },
    { value: '100k+', label: '100K+ followers' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    if (onFiltersChange) onFiltersChange(newFilters);
  };

  const handleTechnologyChange = (tech, checked) => {
    const newTech = checked 
      ? [...filters?.technology, tech]
      : filters?.technology?.filter(t => t !== tech);
    handleFilterChange('technology', newTech);
  };

  const handleBehavioralChange = (behavior, checked) => {
    const newBehaviors = checked 
      ? [...filters?.behavioralIndicators, behavior]
      : filters?.behavioralIndicators?.filter(b => b !== behavior);
    handleFilterChange('behavioralIndicators', newBehaviors);
  };

  const handleSocialMediaChange = (platform, checked) => {
    const newPlatforms = checked 
      ? [...filters?.socialMediaPresence, platform]
      : filters?.socialMediaPresence?.filter(p => p !== platform);
    handleFilterChange('socialMediaPresence', newPlatforms);
  };

  const handleAdvertisingChange = (platform, checked) => {
    const newPlatforms = checked 
      ? [...filters?.advertisingActivity, platform]
      : filters?.advertisingActivity?.filter(p => p !== platform);
    handleFilterChange('advertisingActivity', newPlatforms);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      leadType: 'all',
      industry: '',
      location: '',
      companySize: '',
      revenue: '',
      technology: [],
      keywords: '',
      territory: '',
      behavioralIndicators: [],
      socialMediaPresence: [],
      advertisingActivity: [],
      websiteQuality: '',
      gmbVerified: false,
      adSpendRange: '',
      socialFollowerRange: ''
    };
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters?.leadType !== 'all') count++;
    if (filters?.industry) count++;
    if (filters?.location) count++;
    if (filters?.companySize) count++;
    if (filters?.revenue) count++;
    if (filters?.territory) count++;
    if (filters?.keywords) count++;
    if (filters?.websiteQuality) count++;
    if (filters?.adSpendRange) count++;
    if (filters?.socialFollowerRange) count++;
    if (filters?.gmbVerified) count++;
    count += filters?.technology?.length;
    count += filters?.behavioralIndicators?.length;
    count += filters?.socialMediaPresence?.length;
    count += filters?.advertisingActivity?.length;
    return count;
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Filter" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Search Filters</h3>
          {getActiveFilterCount() > 0 && (
            <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
              {getActiveFilterCount()}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {getActiveFilterCount() > 0 && (
            <Button variant="ghost" size="sm" onClick={clearAllFilters}>
              Clear All
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="md:hidden"
          >
            <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} />
          </Button>
        </div>
      </div>
      {/* Filters Content */}
      <div className={`p-4 space-y-4 ${!isExpanded ? 'hidden md:block' : ''}`}>
        {/* Lead Type Toggle */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Lead Type</label>
          <div className="flex space-x-2">
            <Button
              variant={filters?.leadType === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange('leadType', 'all')}
            >
              All Leads
            </Button>
            <Button
              variant={filters?.leadType === 'b2b' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange('leadType', 'b2b')}
            >
              B2B
            </Button>
            <Button
              variant={filters?.leadType === 'b2c' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange('leadType', 'b2c')}
            >
              B2C
            </Button>
          </div>
        </div>

        {/* Keywords Search */}
        <Input
          label="Keywords"
          type="text"
          placeholder="Enter keywords, job titles, or company names..."
          value={filters?.keywords}
          onChange={(e) => handleFilterChange('keywords', e?.target?.value)}
        />

        {/* Basic Filters Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Select
            label="Industry"
            options={industryOptions}
            value={filters?.industry}
            onChange={(value) => handleFilterChange('industry', value)}
            searchable
          />

          <Input
            label="Location"
            type="text"
            placeholder="City, State, Country..."
            value={filters?.location}
            onChange={(e) => handleFilterChange('location', e?.target?.value)}
          />

          <Select
            label="Territory"
            options={territoryOptions}
            value={filters?.territory}
            onChange={(value) => handleFilterChange('territory', value)}
          />
        </div>

        {/* Company Details Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Company Size"
            options={companySizeOptions}
            value={filters?.companySize}
            onChange={(value) => handleFilterChange('companySize', value)}
          />

          <Select
            label="Revenue Range"
            options={revenueOptions}
            value={filters?.revenue}
            onChange={(value) => handleFilterChange('revenue', value)}
          />
        </div>

        {/* Technology Stack */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground">Technology Stack</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {technologyOptions?.map((tech) => (
              <Checkbox
                key={tech?.value}
                label={tech?.label}
                checked={filters?.technology?.includes(tech?.value)}
                onChange={(e) => handleTechnologyChange(tech?.value, e?.target?.checked)}
              />
            ))}
          </div>
        </div>

        {/* Behavioral Indicators */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground">Behavioral Indicators</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {behavioralOptions?.map((behavior) => (
              <Checkbox
                key={behavior?.value}
                label={behavior?.label}
                checked={filters?.behavioralIndicators?.includes(behavior?.value)}
                onChange={(e) => handleBehavioralChange(behavior?.value, e?.target?.checked)}
              />
            ))}
          </div>
        </div>

        {/* New Section: Social Media Presence */}
        <div className="space-y-3 pt-4 border-t border-border">
          <div className="flex items-center space-x-2">
            <Icon name="Share2" size={16} className="text-blue-500" />
            <label className="text-sm font-medium text-foreground">Social Media Presence</label>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {socialMediaPlatforms?.map((platform) => (
              <Checkbox
                key={platform?.value}
                label={platform?.label}
                checked={filters?.socialMediaPresence?.includes(platform?.value)}
                onChange={(e) => handleSocialMediaChange(platform?.value, e?.target?.checked)}
              />
            ))}
          </div>
          <Select
            label="Social Follower Range"
            options={socialFollowerRanges}
            value={filters?.socialFollowerRange}
            onChange={(value) => handleFilterChange('socialFollowerRange', value)}
          />
        </div>

        {/* New Section: Advertising Activity */}
        <div className="space-y-3 pt-4 border-t border-border">
          <div className="flex items-center space-x-2">
            <Icon name="TrendingUp" size={16} className="text-green-500" />
            <label className="text-sm font-medium text-foreground">Advertising Activity</label>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-2 gap-2">
            {advertisingPlatforms?.map((platform) => (
              <Checkbox
                key={platform?.value}
                label={platform?.label}
                checked={filters?.advertisingActivity?.includes(platform?.value)}
                onChange={(e) => handleAdvertisingChange(platform?.value, e?.target?.checked)}
              />
            ))}
          </div>
          <Select
            label="Monthly Ad Spend"
            options={adSpendRanges}
            value={filters?.adSpendRange}
            onChange={(value) => handleFilterChange('adSpendRange', value)}
          />
        </div>

        {/* New Section: Website & GMB Quality */}
        <div className="space-y-3 pt-4 border-t border-border">
          <div className="flex items-center space-x-2">
            <Icon name="Globe" size={16} className="text-purple-500" />
            <label className="text-sm font-medium text-foreground">Website & GMB</label>
          </div>
          <Select
            label="Website Quality"
            options={websiteQualityOptions}
            value={filters?.websiteQuality}
            onChange={(value) => handleFilterChange('websiteQuality', value)}
          />
          <Checkbox
            label="Google My Business Verified"
            checked={filters?.gmbVerified}
            onChange={(e) => handleFilterChange('gmbVerified', e?.target?.checked)}
          />
        </div>

        {/* Search Button */}
        <div className="pt-4 border-t border-border">
          <Button
            variant="default"
            fullWidth
            loading={isLoading}
            iconName="Search"
            iconPosition="left"
            onClick={() => {
              if (onSearch) {
                onSearch(filters);
              } else if (onFiltersChange) {
                onFiltersChange(filters);
              }
            }}
          >
            {isLoading ? 'Searching...' : 'Search Leads'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SearchFilters;